// File: src/pages/Compare/Compare.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  MapPin, 
  Filter, 
  TrendingUp, 
  TrendingDown,
  DollarSign,
  Users,
  Home,
  Car,
  Building,
  Shield,
  Search,
  Target,
  AlertTriangle,
  CheckCircle,
  Upload,
  Zap
} from 'lucide-react';
import styles from './Compare.module.css';

const Compare = () => {
  const [selectedInsuranceType, setSelectedInsuranceType] = useState('auto');
  const [selectedLocation, setSelectedLocation] = useState('toronto-on');
  const [selectedCoverage, setSelectedCoverage] = useState('comprehensive');
  const [mapData, setMapData] = useState([]);
  const [statsData, setStatsData] = useState({});

  // Insurance types
  const insuranceTypes = [
    { id: 'auto', label: 'Auto Insurance', icon: Car },
    { id: 'home', label: 'Home Insurance', icon: Home },
    { id: 'condo', label: 'Condo Insurance', icon: Building },
    { id: 'tenant', label: 'Tenant Insurance', icon: Shield }
  ];

  // Coverage levels
  const coverageLevels = [
    { id: 'basic', label: 'Basic Coverage' },
    { id: 'comprehensive', label: 'Comprehensive' },
    { id: 'premium', label: 'Premium Coverage' }
  ];

  // Major Canadian cities
  const locations = [
    { id: 'toronto-on', label: 'Toronto, ON', province: 'Ontario' },
    { id: 'vancouver-bc', label: 'Vancouver, BC', province: 'British Columbia' },
    { id: 'montreal-qc', label: 'Montreal, QC', province: 'Quebec' },
    { id: 'calgary-ab', label: 'Calgary, AB', province: 'Alberta' },
    { id: 'ottawa-on', label: 'Ottawa, ON', province: 'Ontario' },
    { id: 'edmonton-ab', label: 'Edmonton, AB', province: 'Alberta' },
    { id: 'mississauga-on', label: 'Mississauga, ON', province: 'Ontario' },
    { id: 'winnipeg-mb', label: 'Winnipeg, MB', province: 'Manitoba' },
    { id: 'hamilton-on', label: 'Hamilton, ON', province: 'Ontario' },
    { id: 'quebec-city-qc', label: 'Quebec City, QC', province: 'Quebec' }
  ];

  // Sample neighborhood data - replace with real API
  const sampleNeighborhoodData = {
    'toronto-on': {
      'auto': {
        'comprehensive': [
          { area: 'Downtown', avgRate: 2840, policies: 1247, trend: 'up', change: 8.2 },
          { area: 'North York', avgRate: 2340, policies: 896, trend: 'up', change: 5.1 },
          { area: 'Scarborough', avgRate: 2180, policies: 743, trend: 'down', change: -2.3 },
          { area: 'Etobicoke', avgRate: 2420, policies: 654, trend: 'up', change: 3.7 },
          { area: 'East York', avgRate: 2590, policies: 432, trend: 'up', change: 6.8 },
          { area: 'York', avgRate: 2720, policies: 387, trend: 'up', change: 9.4 }
        ]
      }
    }
  };

  // Stats for current selection
  const currentStats = {
    avgRate: 2420,
    totalPolicies: 4359,
    priceRange: { min: 1850, max: 3200 },
    monthlyChange: 5.2,
    topPercentileSavings: 890,
    bottomPercentileLoss: 740
  };

  useEffect(() => {
    // Load map data based on current filters
    loadMapData();
  }, [selectedInsuranceType, selectedLocation, selectedCoverage]);

  const loadMapData = () => {
    // Simulate API call
    const data = sampleNeighborhoodData[selectedLocation]?.[selectedInsuranceType]?.[selectedCoverage] || [];
    setMapData(data);
    setStatsData(currentStats);
  };

  const getInsuranceIcon = (type) => {
    const insuranceType = insuranceTypes.find(t => t.id === type);
    return insuranceType ? insuranceType.icon : Car;
  };

  const getCurrentLocation = () => {
    return locations.find(loc => loc.id === selectedLocation)?.label || 'Toronto, ON';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getGradientForRate = (rate) => {
    // Lower rates get green, higher rates get red
    const minRate = Math.min(...mapData.map(d => d.avgRate));
    const maxRate = Math.max(...mapData.map(d => d.avgRate));
    const normalized = (rate - minRate) / (maxRate - minRate);
    
    if (normalized < 0.3) return 'var(--color-grade-a)';
    if (normalized < 0.5) return 'var(--color-grade-b)';
    if (normalized < 0.7) return 'var(--color-grade-c)';
    return 'var(--color-grade-d)';
  };

  return (
    <div className={styles.comparePage}>
      {/* Hero Section */}
      <section className={styles.heroSection}>
        <div className={styles.container}>
          <div className={styles.heroContent}>
            <h1 className={styles.heroTitle}>
              See What Your
              <span className={styles.heroAccent}>Neighbors Actually Pay</span>
            </h1>
            <p className={styles.heroSubtitle}>
              Real insurance rates from real people in your area. 
              Discover if you're getting crushed or crushing it compared to your neighborhood.
            </p>
          </div>
        </div>
      </section>

      {/* Filters Section */}
      <section className={styles.filtersSection}>
        <div className={styles.container}>
          <div className={styles.filtersCard}>
            <div className={styles.filterGroup}>
              <label className={styles.filterLabel}>
                <Filter size={16} />
                Insurance Type
              </label>
              <div className={styles.filterButtons}>
                {insuranceTypes.map((type) => {
                  const IconComponent = type.icon;
                  return (
                    <button
                      key={type.id}
                      className={`${styles.filterButton} ${selectedInsuranceType === type.id ? styles.active : ''}`}
                      onClick={() => setSelectedInsuranceType(type.id)}
                    >
                      <IconComponent size={20} />
                      {type.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className={styles.filterGroup}>
              <label className={styles.filterLabel}>
                <MapPin size={16} />
                Location
              </label>
              <select 
                className={styles.filterSelect}
                value={selectedLocation}
                onChange={(e) => setSelectedLocation(e.target.value)}
              >
                {locations.map((location) => (
                  <option key={location.id} value={location.id}>
                    {location.label}
                  </option>
                ))}
              </select>
            </div>

            <div className={styles.filterGroup}>
              <label className={styles.filterLabel}>
                <Shield size={16} />
                Coverage Level
              </label>
              <select 
                className={styles.filterSelect}
                value={selectedCoverage}
                onChange={(e) => setSelectedCoverage(e.target.value)}
              >
                {coverageLevels.map((level) => (
                  <option key={level.id} value={level.id}>
                    {level.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Overview */}
      <section className={styles.statsSection}>
        <div className={styles.container}>
          <div className={styles.statsGrid}>
            <div className={styles.statCard}>
              <div className={styles.statIcon}>
                <DollarSign />
              </div>
              <div className={styles.statContent}>
                <div className={styles.statValue}>{formatCurrency(statsData.avgRate)}</div>
                <div className={styles.statLabel}>Average Annual Rate</div>
                <div className={styles.statSubtext}>
                  {getCurrentLocation()} • {selectedInsuranceType} insurance
                </div>
              </div>
            </div>

            <div className={styles.statCard}>
              <div className={styles.statIcon}>
                <Users />
              </div>
              <div className={styles.statContent}>
                <div className={styles.statValue}>{statsData.totalPolicies?.toLocaleString()}</div>
                <div className={styles.statLabel}>Policies Analyzed</div>
                <div className={styles.statSubtext}>
                  Real data from real people
                </div>
              </div>
            </div>

            <div className={styles.statCard}>
              <div className={styles.statIcon}>
                <TrendingUp />
              </div>
              <div className={styles.statContent}>
                <div className={`${styles.statValue} ${styles.trending}`}>
                  +{statsData.monthlyChange}%
                </div>
                <div className={styles.statLabel}>Monthly Increase</div>
                <div className={styles.statSubtext}>
                  Rates are rising fast
                </div>
              </div>
            </div>

            <div className={styles.statCard}>
              <div className={styles.statIcon}>
                <Target />
              </div>
              <div className={styles.statContent}>
                <div className={styles.statValue}>
                  {formatCurrency(statsData.priceRange?.min)} - {formatCurrency(statsData.priceRange?.max)}
                </div>
                <div className={styles.statLabel}>Price Range</div>
                <div className={styles.statSubtext}>
                  Wide variation in rates
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Neighborhood Breakdown */}
      <section className={styles.neighborhoodSection}>
        <div className={styles.container}>
          <h2 className={styles.sectionTitle}>
            Neighborhood Breakdown: {getCurrentLocation()}
          </h2>
          <p className={styles.sectionSubtitle}>
            See how insurance rates vary dramatically across different areas of the city
          </p>

          <div className={styles.neighborhoodGrid}>
            {mapData.map((area, index) => (
              <div key={index} className={styles.neighborhoodCard}>
                <div className={styles.neighborhoodHeader}>
                  <h3 className={styles.neighborhoodName}>{area.area}</h3>
                  <div className={styles.neighborhoodTrend}>
                    {area.trend === 'up' ? (
                      <TrendingUp className={styles.trendUp} size={16} />
                    ) : (
                      <TrendingDown className={styles.trendDown} size={16} />
                    )}
                    <span className={area.trend === 'up' ? styles.trendUp : styles.trendDown}>
                      {area.trend === 'up' ? '+' : ''}{area.change}%
                    </span>
                  </div>
                </div>

                <div className={styles.neighborhoodRate}>
                  <div 
                    className={styles.rateValue}
                    style={{ color: getGradientForRate(area.avgRate) }}
                  >
                    {formatCurrency(area.avgRate)}
                  </div>
                  <div className={styles.rateLabel}>avg/year</div>
                </div>

                <div className={styles.neighborhoodMeta}>
                  <div className={styles.metaItem}>
                    <Users size={14} />
                    <span>{area.policies} policies</span>
                  </div>
                  <div className={styles.metaItem}>
                    <MapPin size={14} />
                    <span>{area.area}</span>
                  </div>
                </div>

                <div className={styles.neighborhoodActions}>
                  <button className={styles.compareButton}>
                    <Search size={16} />
                    Compare Details
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Insights Section */}
      <section className={styles.insightsSection}>
        <div className={styles.container}>
          <h2 className={styles.sectionTitle}>Key Insights</h2>
          
          <div className={styles.insightsGrid}>
            <div className={styles.insightCard}>
              <div className={styles.insightIcon}>
                <AlertTriangle />
              </div>
              <h3 className={styles.insightTitle}>Huge Price Variations</h3>
              <p className={styles.insightDescription}>
                Insurance rates can vary by up to {formatCurrency(statsData.priceRange?.max - statsData.priceRange?.min)} 
                within the same city for identical coverage.
              </p>
            </div>

            <div className={styles.insightCard}>
              <div className={styles.insightIcon}>
                <TrendingUp />
              </div>
              <h3 className={styles.insightTitle}>Rising Rates</h3>
              <p className={styles.insightDescription}>
                Average rates increased {statsData.monthlyChange}% this month. 
                If you haven't compared recently, you're likely overpaying.
              </p>
            </div>

            <div className={styles.insightCard}>
              <div className={styles.insightIcon}>
                <CheckCircle />
              </div>
              <h3 className={styles.insightTitle}>Optimization Opportunity</h3>
              <p className={styles.insightDescription}>
                Top 10% of users save an average of {formatCurrency(statsData.topPercentileSavings)} 
                annually by optimizing their coverage.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className={styles.ctaSection}>
        <div className={styles.container}>
          <div className={styles.ctaContent}>
            <h2 className={styles.ctaTitle}>
              Where Do You Stand?
            </h2>
            <p className={styles.ctaSubtitle}>
              Upload your policy to see exactly how your rates compare to your neighbors. 
              Get your A-F grade in 30 seconds.
            </p>
            <div className={styles.ctaButtons}>
              <Link to="/" className={styles.primaryCTA}>
                <Upload size={20} />
                Analyze My Policy
              </Link>
              <Link to="/about" className={styles.secondaryCTA}>
                <Zap size={20} />
                How It Works
              </Link>
            </div>
            <div className={styles.ctaNote}>
              <Shield size={16} />
              <span>Free analysis • Secure & private • No signup required</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Compare;